import axios from 'axios'
import type {
  HeadersInit,
  PathParamStyle,
  PathSerializer,
  Serializers,
  Styles,
} from './serializers'
import type { StandardSchemaValidator } from './standardSchema'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import {
  applyHeaderStyles,
  defaultBodySerializer,
  defaultPathSerializer,
  defaultQuerySerializer,
  isDefaultJsonBody,
  serializeCookies,
} from './serializers'
import { ParseError, validateStandardSchema } from './standardSchema'

/**
 * HTTP status codes treated as a success, everything else is an error.
 */
export type SuccessStatusCode =
  | '200'
  | '201'
  | '202'
  | '203'
  | '204'
  | '205'
  | '206'
  | '207'
  | '208'
  | '226'

/**
 * The success members of a per-status responses record.
 */
export type SuccessOf<TResponses> = TResponses[Extract<keyof TResponses, SuccessStatusCode>]

/**
 * The error members of a per-status responses record, every documented status that is not a 2xx.
 */
export type ErrorOf<TResponses> = TResponses[Exclude<keyof TResponses, SuccessStatusCode>]

/**
 * Converts a response record's string status key to its numeric literal, leaving non-numeric keys like `default` as `number`.
 */
export type ToStatusNumber<TStatus> = TStatus extends `${infer TNumber extends number}`
  ? TNumber
  : number

/**
 * The plain body of a per-status response, unwrapping the `{ contentType; data }` union so an error result keeps the bare body union on `error`.
 */
export type DataOf<T> = T extends { contentType: string; data: infer TData } ? TData : T

/**
 * The success variant for a single status, flattened so the negotiated `contentType` sits next to `data` and `switch (result.contentType)` narrows it.
 */
export type SuccessVariant<TStatus, TEntry, TRequest, TResponse> = TEntry extends {
  contentType: string
  data: unknown
}
  ? TEntry extends { contentType: infer TContentType; data: infer TData }
    ? {
        status: ToStatusNumber<TStatus>
        data: TData
        error: undefined
        contentType: TContentType
        request: TRequest
        response: TResponse
      }
    : never
  : {
      status: ToStatusNumber<TStatus>
      data: TEntry
      error: undefined
      contentType: string | undefined
      request: TRequest
      response: TResponse
    }

/**
 * One result variant for a single documented status, keyed by the numeric `status` so a `switch (result.status)` narrows `data` or `error`.
 */
export type ResultByStatus<
  TResponses,
  TStatus extends keyof TResponses,
  TRequest,
  TResponse,
> = TStatus extends SuccessStatusCode
  ? SuccessVariant<TStatus, TResponses[TStatus], TRequest, TResponse>
  : {
      status: ToStatusNumber<TStatus>
      data: undefined
      error: DataOf<TResponses[TStatus]>
      contentType: string | undefined
      request: TRequest
      response: TResponse
    }

/**
 * The union of every documented status' result variant.
 */
export type ResultUnion<TResponses, TRequest, TResponse> = {
  [TStatus in keyof TResponses]: ResultByStatus<TResponses, TStatus, TRequest, TResponse>
}[keyof TResponses]

/**
 * The union of just the success (2xx) status variants, selected by status code so an untyped error payload can never widen `data`.
 */
export type SuccessResultUnion<TResponses, TRequest, TResponse> = {
  [TStatus in Extract<keyof TResponses, SuccessStatusCode>]: ResultByStatus<
    TResponses,
    TStatus,
    TRequest,
    TResponse
  >
}[Extract<keyof TResponses, SuccessStatusCode>]

/**
 * The shape every generated function returns, discriminated by the top-level `status`, narrowing to the 2xx variants under `throwOnError` and to every documented status without it.
 */
export type RequestResult<
  TResponses,
  ThrowOnError extends boolean = true,
  TRequest = AxiosRequestConfig,
  TResponse = AxiosResponse,
> = ThrowOnError extends true
  ? [SuccessResultUnion<TResponses, TRequest, TResponse>] extends [never]
    ? {
        status: number
        data: SuccessOf<TResponses>
        error: undefined
        contentType: string | undefined
        request: TRequest
        response: TResponse
      }
    : SuccessResultUnion<TResponses, TRequest, TResponse>
  : [ResultUnion<TResponses, TRequest, TResponse>] extends [never]
    ? {
        status: number
        data: undefined
        error: undefined
        contentType: string | undefined
        request: TRequest
        response: TResponse
      }
    : ResultUnion<TResponses, TRequest, TResponse>

/**
 * A `RequestResult` promise with an extra `unwrap()` method that resolves to the success body.
 */
export type Unwrappable<T extends { data: unknown; error: unknown }> = Promise<T> & {
  unwrap: () => Promise<Extract<T, { error: undefined }>['data']>
}

/**
 * Attaches `unwrap()` to a result promise, which rejects with `error` when the result carried one.
 *
 * @example Full result
 * `const { data, error } = await getPetById({ path: { petId: 1 } })`
 *
 * @example Success body only
 * `const pet = await getPetById({ path: { petId: 1 } }).unwrap()`
 */
export function withUnwrap<T extends { data: unknown; error: unknown }>(
  promise: Promise<T>,
): Unwrappable<T> {
  const unwrappable = promise as Unwrappable<T>
  unwrappable.unwrap = () =>
    promise.then((result) => {
      if (result.error !== undefined) throw result.error
      return result.data as Extract<T, { error: undefined }>['data']
    })
  return unwrappable
}

/**
 * The shape a generated operation returns with `returnType: 'data'`: the success body when
 * `throwOnError` is true, or the full `RequestResult` when it is false.
 */
export type UnwrappedResult<
  TResponses,
  ThrowOnError extends boolean = true,
  TRequest = AxiosRequestConfig,
  TResponse = AxiosResponse,
> = ThrowOnError extends true
  ? RequestResult<TResponses, true, TRequest, TResponse>['data']
  : RequestResult<TResponses, ThrowOnError, TRequest, TResponse>

/**
 * Returns the success body when `throwOnError` is true, or the full result when it is false.
 * Backs generated operations with `returnType: 'data'`.
 */
export function unwrapResult<T extends { data: unknown; error: unknown }>(
  promise: Promise<T>,
  throwOnError: boolean | undefined,
): Promise<T | T['data']> {
  return promise.then((result) => ((throwOnError ?? true) ? result.data : result))
}

/**
 * The data-shaped keys of the grouped options object, which `Options` re-adds typed per operation.
 */
export type DataShape = {
  body?: unknown
  cookies?: unknown
  headers?: unknown
  path?: unknown
  query?: unknown
}

export type ResponseType =
  | 'arraybuffer'
  | 'blob'
  | 'document'
  | 'json'
  | 'text'
  | 'stream'
  | 'formdata'

/**
 * Turns a raw response body into a parsed value, registered per media type as a codec's `deserialize` to handle formats the runtime does not decode itself.
 */
export type Deserializer<T = unknown> = (raw: unknown, contentType: string) => T | Promise<T>

/**
 * Serializes a request body for a single media type, registered per content type as a codec's `serialize` to encode formats the default serializer does not handle.
 */
export type ContentBodySerializer = (body: unknown, contentType?: string) => unknown

/**
 * A per-content-type codec registered on `codecs`, keyed by content type. `serialize` encodes the
 * request body for that media type and `deserialize` decodes the response body. Either half is
 * optional, so a codec can handle one direction.
 */
export type Codec = {
  serialize?: ContentBodySerializer
  deserialize?: Deserializer
}

/**
 * The per-call content type selection, where a bare string sets the request content type and the object form also sets the response format sent as `Accept`.
 */
export type ContentType = string | { request?: string; response?: string }

/**
 * A Standard Schema validator (zod, valibot, arktype) that parses a value before it is sent or after
 * it is received. `runValidator` runs it through `validateStandardSchema`. Wired through the per-call
 * `validator.request` / `validator.response` / `validator.error` hooks (`error` runs on the error body when a
 * non-2xx call does not throw).
 */
export type Validator<T = unknown> = StandardSchemaValidator<T>

/**
 * The failing body and the call it came from, handed to `onValidationError` alongside the `ParseError`.
 * `direction` says which slot rejected it: the request body, the success body, or the error body.
 */
export type ValidationErrorContext = {
  value: unknown
  direction: 'request' | 'response' | 'error'
  method?: string
  url?: string
  status?: number
}

/**
 * Decides what a failed validation does. Returning nothing rethrows the `ParseError`; returning
 * `{ value }` resolves the call with that value instead, so a drifted body can be reported and still
 * delivered. The box keeps an explicit `{ value: undefined }` substitution distinct from declining.
 */
export type ValidationErrorHandler = (
  error: ParseError,
  context: ValidationErrorContext,
) => { value: unknown } | void | Promise<{ value: unknown } | void>

/**
 * A resolved security scheme carried on each generated call's `security` array and passed to the `auth` resolver.
 */
export type Auth = {
  type: 'http' | 'apiKey' | 'oauth2' | 'openIdConnect'
  scheme?: 'bearer' | 'basic'
  name?: string
  in?: 'header' | 'query' | 'cookie'
}

/**
 * The raw token a consumer returns for a scheme (or `user:password` for basic), or `undefined` to skip it.
 */
export type AuthToken = string | undefined

/**
 * Resolves the token for a security scheme, either a static token or a callback called per scheme until one returns a token.
 */
export type AuthResolver = AuthToken | ((auth: Auth) => AuthToken | Promise<AuthToken>)

/**
 * Extra axios config the runtime spreads onto every request, an escape hatch for per-call fields it does not set itself such as `timeout`, `proxy`, and the progress callbacks.
 */
export type AxiosOptions = AxiosRequestConfig

/**
 * The request a generated function hands to the runtime, with `body` / `headers` / `path` / `query` from the grouped options.
 */
export type RequestConfig<
  TBody = unknown,
  TRequest = AxiosRequestConfig,
  TResponse = AxiosResponse,
> = {
  baseURL?: string
  url?: string
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS' | 'HEAD' | 'QUERY'
  path?: unknown
  query?: unknown
  params?: unknown
  cookies?: unknown
  body?: TBody
  headers?: unknown
  styles?: Styles
  signal?: AbortSignal
  options?: AxiosOptions
  contentType?: ContentType
  responseType?: ResponseType
  throwOnError?: boolean
  validateStatus?: (status: number) => boolean
  client?: ClientInstance<TRequest, TResponse>
  transport?: AxiosInstance
  serializer?: Serializers
  codecs?: Record<string, Codec>
  validator?: { request?: Validator; response?: Validator; error?: Validator }
  onValidationError?: ValidationErrorHandler
  security?: Array<Auth>
  auth?: AuthResolver
}

/**
 * The grouped options object passed to every generated function: the request config minus the
 * data-shaped keys and the literal `url`, plus the per-operation `<Name>Request`.
 */
export type Options<
  TData extends DataShape,
  ThrowOnError extends boolean = true,
  TRequest = AxiosRequestConfig,
  TResponse = AxiosResponse,
> = Omit<RequestConfig<unknown, TRequest, TResponse>, keyof DataShape | 'url'> &
  TData & {
    client?: ClientInstance<TRequest, TResponse>
    throwOnError?: ThrowOnError
  }

/**
 * Client-level configuration shared by every call an instance makes, overridden by the per-call `RequestConfig`.
 */
export type ClientConfig = {
  baseURL?: string
  headers?: HeadersInit
  options?: AxiosOptions
  throwOnError?: boolean
  validateStatus?: (status: number) => boolean
  transport?: AxiosInstance
  serializer?: Serializers
  codecs?: Record<string, Codec>
  onValidationError?: ValidationErrorHandler
  auth?: AuthResolver
}

/**
 * The result a resolved call produces before it is cast to `RequestResult` by the generated wrapper.
 */
export type CallResult<TRequest = AxiosRequestConfig, TResponse = AxiosResponse> = {
  status: number
  data: unknown
  error: unknown
  contentType: string | undefined
  request: TRequest
  response: TResponse
}

export type InterceptorFn<T, TRequest = AxiosRequestConfig, TResponse = AxiosResponse> = (
  value: T,
  requestConfig?: RequestConfig<unknown, TRequest, TResponse>,
) => T | Promise<T>

/**
 * A single interceptor channel with a transport-agnostic `use` / `eject` / `update` API, backed by axios's native interceptor managers.
 */
export type InterceptorChannel<T, TRequest = AxiosRequestConfig, TResponse = AxiosResponse> = {
  use: (fn: InterceptorFn<T, TRequest, TResponse>) => number
  eject: (id: number) => void
  update: (id: number, fn: InterceptorFn<T, TRequest, TResponse>) => void
}

/**
 * The three interceptor channels every client instance exposes, wrapping axios's native managers with `error` mapped onto the response rejection handler.
 */
export type Interceptors<TRequest = AxiosRequestConfig, TResponse = AxiosResponse> = {
  request: InterceptorChannel<InternalAxiosRequestConfig, TRequest, TResponse>
  response: InterceptorChannel<AxiosResponse, TRequest, TResponse>
  error: InterceptorChannel<AxiosError, TRequest, TResponse>
}

/**
 * A client instance: the callable send plus configuration, interceptors, and an isolated
 * `createClient` factory.
 */
export type ClientInstance<TRequest = AxiosRequestConfig, TResponse = AxiosResponse> = {
  <TBody = unknown>(
    config: RequestConfig<TBody, TRequest, TResponse>,
  ): Promise<CallResult<TRequest, TResponse>>
  getConfig: () => ClientConfig
  setConfig: (config: ClientConfig) => ClientConfig
  getUrl: <TBody = unknown>(config: RequestConfig<TBody, TRequest, TResponse>) => string
  interceptors: Interceptors<TRequest, TResponse>
  createClient: (config?: ClientConfig) => ClientInstance<TRequest, TResponse>
}

/**
 * Thrown for a non-2xx response, so a resolved call always means success.
 */
export class ResponseError<
  TError = unknown,
  TRequest = AxiosRequestConfig,
  TResponse = AxiosResponse,
> extends Error {
  data: TError
  status: number
  statusText: string
  contentType: string | undefined
  request: TRequest
  response: TResponse

  constructor(config: {
    data: TError
    status: number
    statusText: string
    contentType?: string
    request: TRequest
    response: TResponse
    method?: string
    url?: string
  }) {
    // The query and hash are left out: sensitive query parameters or fragments must not reach logs.
    const cleanUrl = config.url?.split(/[?#]/)[0]
    const target = [config.method, cleanUrl].filter(Boolean).join(' ')
    const statusText = config.statusText?.trim()
    super(
      `${target ? `${target} failed` : 'Request failed'} with status ${config.status}${statusText ? ` ${statusText}` : ''}`,
    )
    this.name = 'ResponseError'
    this.data = config.data
    this.status = config.status
    this.statusText = config.statusText
    this.contentType = config.contentType
    this.request = config.request
    this.response = config.response
  }

  /**
   * Matches on `name`, not `instanceof`: every generated client bundles its own `ResponseError` class.
   */
  static is(error: unknown): error is ResponseError<unknown, unknown, unknown> {
    return error instanceof Error && error.name === 'ResponseError'
  }
}

export type ResponseErrorConfig<TError = unknown> = ResponseError<TError>

function serializeHeaders(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {}
  const entries = Array.isArray(headers) ? headers : Object.entries(headers)
  const result: Record<string, string> = {}
  for (const [key, value] of entries) {
    if (value === undefined || value === null) continue
    result[key] =
      typeof value === 'string'
        ? value
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value)
  }
  return result
}

function mergeHeaders(...sources: Array<HeadersInit | undefined>): Record<string, string> {
  return Object.assign({}, ...sources.map(serializeHeaders))
}

function getHeader(headers: Record<string, string>, name: string): string | undefined {
  const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase())
  return key ? headers[key] : undefined
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
  return Object.keys(headers).some((k) => k.toLowerCase() === name.toLowerCase())
}

/**
 * Joins the URL parts, interpolates URL-encoded `{param}` segments, and appends the serialized query, backing `getUrl`.
 */
function serializeUrl({
  parts,
  pathParams,
  search,
  pathSerializer = defaultPathSerializer,
  pathStyles,
}: {
  parts: Array<string | undefined>
  pathParams: Record<string, unknown>
  search: string
  pathSerializer?: PathSerializer
  pathStyles?: Record<string, PathParamStyle>
}): string {
  const path = parts
    .filter(Boolean)
    .join('')
    .replace(/\{([^{}]+)\}/g, (_, key: string) =>
      pathSerializer({ name: key, value: pathParams[key], options: pathStyles?.[key] }),
    )
  return path + (search ? `?${search}` : '')
}

/**
 * Wraps an axios interceptor registration behind the shared `use` / `eject` / `update` API, mapping a stable external id onto axios's own so `update` can swap a handler in place.
 * `detach` / `attach` move every registered handler to another instance, keeping the external ids.
 */
function createInterceptorChannel<T, TRequest, TResponse>(
  register: (fn: InterceptorFn<T, TRequest, TResponse>) => number,
  ejectNative: (id: number) => void,
  getRequestConfig: (value: T) => RequestConfig<unknown, TRequest, TResponse> | undefined,
  getContextId: (value: T) => number | undefined,
  setContextId: (value: T, id: number | undefined) => void,
  nextSeq?: () => number,
): InterceptorChannel<T, TRequest, TResponse> & {
  detach: () => void
  attach: () => void
  getEntries: () => Array<{ seq: number; attach: () => void }>
} {
  const ids = new Map<number, number>()
  const handlers = new Map<number, { fn: InterceptorFn<T, TRequest, TResponse>; seq: number }>()
  let counter = 0
  const getSeq = nextSeq ?? (() => ++counter)
  const registerWithContext = (fn: InterceptorFn<T, TRequest, TResponse>) =>
    register(async (value) => {
      const contextId = getContextId(value)
      const result = await fn(value, getRequestConfig(value))
      if (contextId !== undefined) setContextId(result, contextId)
      return result
    })

  return {
    use(fn) {
      const id = ++counter
      const seq = getSeq()
      handlers.set(id, { fn, seq })
      ids.set(id, registerWithContext(fn))
      return id
    },
    eject(id) {
      const nativeId = ids.get(id)
      if (nativeId === undefined) return
      ejectNative(nativeId)
      ids.delete(id)
      handlers.delete(id)
    },
    update(id, fn) {
      const nativeId = ids.get(id)
      if (nativeId === undefined) return
      ejectNative(nativeId)
      const seq = getSeq()
      handlers.set(id, { fn, seq })
      ids.set(id, registerWithContext(fn))
    },
    detach() {
      for (const nativeId of ids.values()) ejectNative(nativeId)
      ids.clear()
    },
    attach() {
      const sorted = Array.from(handlers.entries()).sort((a, b) => a[1].seq - b[1].seq)
      for (const [id, entry] of sorted) ids.set(id, registerWithContext(entry.fn))
    },
    getEntries() {
      return Array.from(handlers.entries()).map(([id, entry]) => ({
        seq: entry.seq,
        attach: () => ids.set(id, registerWithContext(entry.fn)),
      }))
    },
  }
}

/**
 * Walks the per-operation security in order and places the first resolved token on the request, mutating `headers` / `query` in place.
 */
export async function resolveAuth(params: {
  security: Array<Auth> | undefined
  auth: AuthResolver | undefined
  headers: Record<string, string>
  query: Record<string, unknown>
}): Promise<void> {
  const { security, auth, headers, query } = params
  if (!security?.length || auth === undefined) return

  for (const scheme of security) {
    const token = typeof auth === 'function' ? await auth(scheme) : auth
    if (token === undefined) continue

    if (scheme.type === 'apiKey') {
      const name = scheme.name ?? 'Authorization'
      if (scheme.in === 'query') {
        if (query[name] === undefined) query[name] = token
      } else if (scheme.in === 'cookie') {
        headers['Cookie'] = [headers['Cookie'], `${name}=${token}`].filter(Boolean).join('; ')
      } else if (!hasHeader(headers, name)) {
        headers[name] = token
      }
    } else if (!hasHeader(headers, 'Authorization')) {
      headers['Authorization'] =
        scheme.scheme === 'basic' ? `Basic ${btoa(token)}` : `Bearer ${token}`
    }
    return
  }
}

async function runValidator<T>({
  validator,
  value,
  context,
  onValidationError,
}: {
  validator: Validator<T> | undefined
  value: T
  context: Omit<ValidationErrorContext, 'value'>
  onValidationError: ValidationErrorHandler | undefined
}): Promise<T> {
  if (!validator) return value
  try {
    return await validateStandardSchema(validator, value)
  } catch (error) {
    if (!onValidationError || !(error instanceof ParseError)) throw error
    const handled = await onValidationError(error, { ...context, value })
    if (!handled) throw error
    return handled.value as T
  }
}

/**
 * The base media type of a `Content-Type` value, lowercased and stripped of any `; charset=...` parameters.
 */
function baseContentType(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  return value.split(';')[0]!.trim().toLowerCase() || undefined
}

/**
 * Reads the negotiated response content type from the response headers as a base media type.
 */
function getResponseContentType(headers: Record<string, unknown> | undefined): string | undefined {
  if (!headers) return undefined
  const value = headers['content-type'] ?? headers['Content-Type']
  return baseContentType(typeof value === 'string' ? value : undefined)
}

/**
 * Normalizes the `contentType` option to its `{ request, response }` form, treating a bare string as the request content type.
 */
function resolveContentType(contentType: ContentType | undefined): {
  request?: string
  response?: string
} {
  if (typeof contentType === 'string') return { request: contentType }
  return contentType ?? {}
}

/**
 * The per-concern serializers for a call, the per-call serializer winning over the client's and
 * falling back to the defaults.
 */
function resolveSerializers({
  config,
  requestConfig,
}: {
  config: { serializer?: Serializers }
  requestConfig: { serializer?: Serializers }
}) {
  return {
    querySerializer:
      requestConfig.serializer?.query ?? config.serializer?.query ?? defaultQuerySerializer,
    bodySerializer:
      requestConfig.serializer?.body ?? config.serializer?.body ?? defaultBodySerializer,
    pathSerializer:
      requestConfig.serializer?.path ?? config.serializer?.path ?? defaultPathSerializer,
  }
}

/**
 * Resolves everything a call needs before it touches axios: merged headers with the negotiated
 * content type, auth on headers or query, serialized cookies, the validated and serialized body,
 * and the final axios request config with `throwOnError` riding `validateStatus`.
 */
async function resolveRequest<TBody, TRequest, TResponse>({
  config,
  requestConfig,
}: {
  config: ClientConfig
  requestConfig: RequestConfig<TBody, TRequest, TResponse>
}): Promise<{
  axiosConfig: AxiosRequestConfig
  codecs: Record<string, Codec>
  throwOnError: boolean
}> {
  const { querySerializer, bodySerializer, pathSerializer } = resolveSerializers({
    config,
    requestConfig,
  })
  const codecs = { ...config.codecs, ...requestConfig.codecs }

  const headers = mergeHeaders(
    config.headers,
    applyHeaderStyles(
      requestConfig.headers as HeadersInit | undefined,
      requestConfig.styles?.header,
    ),
  )
  const { request: requestContentTypeOption, response: responseContentType } = resolveContentType(
    requestConfig.contentType,
  )
  const requestContentType = requestContentTypeOption ?? getHeader(headers, 'content-type')
  if (responseContentType && !hasHeader(headers, 'accept')) {
    headers['Accept'] = responseContentType
  }

  const query: Record<string, unknown> = {
    ...((requestConfig.query ?? requestConfig.params) as Record<string, unknown> | undefined),
  }

  await resolveAuth({
    security: requestConfig.security,
    auth: requestConfig.auth ?? config.auth,
    headers,
    query,
  })

  if (requestConfig.cookies) {
    const cookie = serializeCookies(
      requestConfig.cookies as Record<string, unknown>,
      requestConfig.styles?.cookie,
    )
    if (cookie) headers['Cookie'] = [headers['Cookie'], cookie].filter(Boolean).join('; ')
  }

  const validatedBody = await runValidator({
    validator: requestConfig.validator?.request,
    value: requestConfig.body,
    context: { direction: 'request', method: requestConfig.method, url: requestConfig.url },
    onValidationError: requestConfig.onValidationError ?? config.onValidationError,
  })
  const requestContentTypeBase = baseContentType(requestContentType)
  const contentCodec = requestContentTypeBase ? codecs[requestContentTypeBase] : undefined
  const usesDefaultBodySerializer =
    !contentCodec?.serialize && bodySerializer === defaultBodySerializer
  const body = contentCodec?.serialize
    ? contentCodec.serialize(validatedBody, requestContentType)
    : bodySerializer({
        body: validatedBody,
        contentType: requestContentType,
        encoding: requestConfig.styles?.body,
      })
  // A FormData body must keep its Content-Type unset so axios appends the multipart boundary.
  if (body instanceof FormData) {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === 'content-type') delete headers[key]
    }
  } else if (requestContentTypeOption) {
    headers['Content-Type'] = requestContentTypeOption
  } else if (body instanceof URLSearchParams && !hasHeader(headers, 'content-type')) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded'
  } else if (
    usesDefaultBodySerializer &&
    isDefaultJsonBody(validatedBody) &&
    !hasHeader(headers, 'content-type')
  ) {
    headers['Content-Type'] = 'application/json'
  }

  const pathParams = (requestConfig.path ?? {}) as Record<string, unknown>
  const url = (requestConfig.url ?? '').replace(/\{([^{}]+)\}/g, (_, key: string) =>
    pathSerializer({
      name: key,
      value: pathParams[key],
      options: requestConfig.styles?.path?.[key],
    }),
  )

  const throwOnError = requestConfig.throwOnError ?? config.throwOnError ?? true
  const validateStatus =
    requestConfig.validateStatus ??
    config.validateStatus ??
    (throwOnError ? (status: number) => status >= 200 && status < 300 : () => true)

  const options =
    config.options || requestConfig.options
      ? { ...config.options, ...requestConfig.options }
      : undefined

  const axiosConfig: AxiosRequestConfig = {
    ...options, // timeout, proxy, maxRedirects, decompress, onUploadProgress, …
    url,
    baseURL: requestConfig.baseURL ?? config.baseURL,
    method: requestConfig.method ?? 'GET',
    headers,
    params: query,
    paramsSerializer: (params) =>
      querySerializer(params as Record<string, unknown>, requestConfig.styles?.query),
    data: body,
    // Kubb already serialized the body; only URLSearchParams needs a string, since axios's Node adapter rejects it.
    transformRequest: (data) => (data instanceof URLSearchParams ? data.toString() : data),
    signal: requestConfig.signal,
    responseType: requestConfig.responseType,
    validateStatus,
  }

  // Only the fetch adapter exposes a streaming `response.data` (a ReadableStream) in the browser.
  // The default XHR adapter buffers the whole body. Default streams to it, but respect an explicit adapter.
  if (requestConfig.responseType === 'stream' && !axiosConfig.adapter) {
    axiosConfig.adapter = 'fetch'
  }

  return { axiosConfig, codecs, throwOnError }
}

/**
 * Turns an axios response into the call result: decodes the body through the matching codec and
 * validates the success or error body. A thrown axios error never reaches this, so `error` here is
 * always the body of a non-2xx response that `validateStatus` let through.
 */
async function settleResponse<TRequest, TResponse>({
  response,
  codecs,
  validator,
  onValidationError,
}: {
  response: AxiosResponse
  codecs: Record<string, Codec>
  validator: { response?: Validator; error?: Validator } | undefined
  onValidationError: ValidationErrorHandler | undefined
}): Promise<CallResult<TRequest, TResponse>> {
  const isSuccess = response.status >= 200 && response.status < 300
  const contentType = getResponseContentType(response.headers as Record<string, unknown>)
  let decoded: unknown = response.data
  if (contentType) {
    const codec = codecs[contentType]
    if (codec?.deserialize) decoded = await codec.deserialize(response.data, contentType)
  }
  const validationContext = {
    method: response.config?.method?.toUpperCase(),
    url: response.config?.url,
    status: response.status,
  }
  const data = isSuccess
    ? await runValidator({
        validator: validator?.response,
        value: decoded,
        context: { direction: 'response', ...validationContext },
        onValidationError,
      })
    : undefined
  const error = isSuccess
    ? undefined
    : await runValidator({
        validator: validator?.error,
        value: decoded,
        context: { direction: 'error', ...validationContext },
        onValidationError,
      })
  return {
    status: response.status,
    data,
    error,
    contentType,
    request: response.config as TRequest,
    response: response as TResponse,
  }
}

/**
 * Builds the shared client core bound to an axios instance (defaulting to `defaultTransport`, else `axios.create()`), with `throwOnError` riding axios's `validateStatus`.
 */
export function createClientCore<TRequest = AxiosRequestConfig, TResponse = AxiosResponse>(
  options: { defaultTransport?: AxiosInstance } & ClientConfig = {},
): ClientInstance<TRequest, TResponse> {
  const { defaultTransport, ...initialConfig } = options
  let config: ClientConfig = initialConfig
  const baseInstance = config.transport ?? defaultTransport ?? axios.create()
  let instance = baseInstance
  const requestContexts = new Map<number, RequestConfig<unknown, TRequest, TResponse>>()
  let requestContextId = 0
  const getContextId = (value: AxiosRequestConfig | AxiosResponse | AxiosError) => {
    if (!value || typeof value !== 'object') return undefined
    const axiosConfig = 'config' in value ? value.config : value
    return (axiosConfig as (AxiosRequestConfig & { __kubbRequestContext?: number }) | undefined)
      ?.__kubbRequestContext
  }
  const getRequestConfig = (value: AxiosRequestConfig | AxiosResponse | AxiosError) => {
    const id = getContextId(value)
    return id === undefined ? undefined : requestContexts.get(id)
  }
  const setContextId = (
    value: AxiosRequestConfig | AxiosResponse | AxiosError,
    id: number | undefined,
  ) => {
    if (!value || typeof value !== 'object') return
    const axiosConfig = 'config' in value ? value.config : value
    if (!axiosConfig) return
    const contextualConfig = axiosConfig as AxiosRequestConfig & { __kubbRequestContext?: number }
    if (id === undefined) delete contextualConfig.__kubbRequestContext
    else contextualConfig.__kubbRequestContext = id
  }

  let sequenceCounter = 0
  const nextSeq = () => ++sequenceCounter

  // Read at call time so the channels follow a transport swapped in through setConfig.
  const channels = {
    request: createInterceptorChannel<InternalAxiosRequestConfig, TRequest, TResponse>(
      (fn) => instance.interceptors.request.use(fn),
      (id) => instance.interceptors.request.eject(id),
      getRequestConfig,
      getContextId,
      setContextId,
      nextSeq,
    ),
    response: createInterceptorChannel<AxiosResponse, TRequest, TResponse>(
      (fn) => instance.interceptors.response.use(fn),
      (id) => instance.interceptors.response.eject(id),
      getRequestConfig,
      getContextId,
      setContextId,
      nextSeq,
    ),
    error: createInterceptorChannel<AxiosError, TRequest, TResponse>(
      (fn) =>
        instance.interceptors.response.use(undefined, (error: unknown) =>
          Promise.resolve(fn(error as AxiosError)).then((result) =>
            Promise.reject(result ?? error),
          ),
        ),
      (id) => instance.interceptors.response.eject(id),
      getRequestConfig,
      getContextId,
      setContextId,
      nextSeq,
    ),
  }
  const channelList = [channels.request, channels.response, channels.error]
  const interceptors: Interceptors<TRequest, TResponse> = {
    request: {
      use: channels.request.use,
      eject: channels.request.eject,
      update: channels.request.update,
    },
    response: {
      use: channels.response.use,
      eject: channels.response.eject,
      update: channels.response.update,
    },
    error: { use: channels.error.use, eject: channels.error.eject, update: channels.error.update },
  }

  const client = (async <TBody = unknown>(
    requestConfig: RequestConfig<TBody, TRequest, TResponse>,
  ): Promise<CallResult<TRequest, TResponse>> => {
    const activeInstance = requestConfig.transport ?? instance
    const { axiosConfig, codecs, throwOnError } = await resolveRequest({ config, requestConfig })
    const contextId = ++requestContextId
    requestContexts.set(contextId, requestConfig)
    ;(axiosConfig as AxiosRequestConfig & { __kubbRequestContext: number }).__kubbRequestContext =
      contextId

    try {
      try {
        const response = await activeInstance.request<unknown, AxiosResponse>(axiosConfig)
        const result = await settleResponse<TRequest, TResponse>({
          response,
          codecs,
          validator: requestConfig.validator,
          onValidationError: requestConfig.onValidationError ?? config.onValidationError,
        })
        setContextId(response, undefined)
        return result
      } catch (error) {
        const axiosError = error as AxiosError
        setContextId(axiosError, undefined)
        if (axiosError.response) setContextId(axiosError.response, undefined)
        if (throwOnError && axiosError.response) {
          throw new ResponseError({
            data: axiosError.response.data,
            status: axiosError.response.status,
            statusText: axiosError.response.statusText,
            contentType: getResponseContentType(
              axiosError.response.headers as Record<string, unknown>,
            ),
            request: axiosError.config as TRequest,
            response: axiosError.response as TResponse,
            method: axiosError.config?.method?.toUpperCase(),
            url: [axiosError.config?.baseURL?.replace(/\/+$/, ''), axiosError.config?.url]
              .filter(Boolean)
              .join(''),
          })
        }
        throw error
      }
    } finally {
      setContextId(axiosConfig, undefined)
      requestContexts.delete(contextId)
    }
  }) as ClientInstance<TRequest, TResponse>

  client.getConfig = () => config
  client.setConfig = (next) => {
    config = {
      ...config,
      ...next,
      headers: { ...serializeHeaders(config.headers), ...serializeHeaders(next.headers) },
    }
    const nextInstance = config.transport ?? baseInstance
    if (nextInstance !== instance) {
      for (const channel of channelList) channel.detach()
      instance = nextInstance
      channels.request.attach()
      const responseEntries = [
        ...channels.response.getEntries(),
        ...channels.error.getEntries(),
      ].sort((a, b) => a.seq - b.seq)
      for (const entry of responseEntries) entry.attach()
    }
    return config
  }
  client.getUrl = (requestConfig) => {
    const { querySerializer, pathSerializer } = resolveSerializers({ config, requestConfig })
    const query: Record<string, unknown> = {
      ...((requestConfig.query ?? requestConfig.params) as Record<string, unknown> | undefined),
    }
    return serializeUrl({
      parts: [requestConfig.baseURL ?? config.baseURL, requestConfig.url],
      pathParams: (requestConfig.path ?? {}) as Record<string, unknown>,
      search: querySerializer(query, requestConfig.styles?.query),
      pathSerializer,
      pathStyles: requestConfig.styles?.path,
    })
  }
  client.interceptors = interceptors
  client.createClient = (next) => createClientCore<TRequest, TResponse>({ ...config, ...next })

  return client
}

/**
 * The axios instance the generated `client` sends through. Register axios interceptors on it or reuse it inside your own transport.
 */
export const defaultTransport = axios.create()

/**
 * One decoded Server-Sent Event, with `data` parsed as JSON when valid and kept as the raw string otherwise.
 */
export type ServerSentEvent<TData = unknown> = {
  data: TData
  event?: string
  id?: string
  retry?: number
}

async function* readBytes(
  stream: ReadableStream<Uint8Array> | AsyncIterable<Uint8Array>,
): AsyncGenerator<Uint8Array> {
  if (!('getReader' in stream)) {
    yield* stream
    return
  }

  const reader = stream.getReader()
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) return
      yield value
    }
  } finally {
    await reader.cancel().catch(() => {})
  }
}

function parseEvent<TData>(raw: string): ServerSentEvent<TData> | undefined {
  const data: Array<string> = []
  const event: ServerSentEvent<TData> = { data: undefined as TData }
  let seen = false

  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue
    seen = true
    const index = line.indexOf(':')
    const field = index === -1 ? line : line.slice(0, index)
    const value = index === -1 ? '' : line.slice(index + 1).replace(/^ /, '')
    if (field === 'data') data.push(value)
    else if (field === 'event') event.event = value
    else if (field === 'id') event.id = value
    else if (field === 'retry' && Number.isFinite(Number(value))) event.retry = Number(value)
  }

  if (!seen) return undefined

  if (data.length) {
    const joined = data.join('\n')
    try {
      event.data = JSON.parse(joined) as TData
    } catch {
      event.data = joined as TData
    }
  }
  return event
}

/**
 * Parses a `text/event-stream` body into typed Server-Sent Events, consumed with `for await` and stopped early by breaking the loop.
 */
export async function* parseEventStream<TData = unknown>(
  stream: ReadableStream<Uint8Array> | AsyncIterable<Uint8Array>,
): AsyncGenerator<ServerSentEvent<TData>> {
  const decoder = new TextDecoder()
  const normalize = (text: string) => text.replace(/\r\n|\r/g, '\n')
  let buffer = ''

  for await (const chunk of readBytes(stream)) {
    const blocks = normalize(buffer + decoder.decode(chunk, { stream: true })).split('\n\n')
    buffer = blocks.pop() ?? ''
    for (const block of blocks) {
      const event = parseEvent<TData>(block)
      if (event) yield event
    }
  }

  const event = parseEvent<TData>(normalize(buffer + decoder.decode()))
  if (event) yield event
}

/**
 * The resolved shape returned by a generated `text/event-stream` operation: the typed event
 * `stream` plus the native `response`.
 */
export type EventStreamResult<TData = unknown, TResponse = AxiosResponse> = {
  stream: AsyncGenerator<ServerSentEvent<TData>>
  response: TResponse
}

/**
 * Wraps a transport result whose `data` is a streaming body into an `EventStreamResult`, exposing
 * the parsed events as a typed async iterator. Generated SSE operations call this.
 */
export async function toEventStream<TData = unknown>(
  result: Promise<{ data: unknown; response: AxiosResponse }>,
): Promise<EventStreamResult<TData>> {
  const { data, response } = await result
  return {
    response,
    stream: parseEventStream<TData>(data as ReadableStream<Uint8Array> | AsyncIterable<Uint8Array>),
  }
}

export const client = createClientCore({ defaultTransport })

export const createClient = (config?: Parameters<typeof client.createClient>[0]) =>
  client.createClient(config)

client.setConfig({ baseURL: 'https://staging.dora.co' })
